import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Mail, MessageCircle, CalendarClock, Loader2, FileText, MapPin,
  ExternalLink, Download, Send, Eye, Sparkles,
} from "lucide-react";
import { api, authHeaders, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { Panel, SectionLabel, inputCls } from "@/components/admin/cc";
import { CategoryBadge, ScoreBadge } from "@/components/admin/badges";
import { L, eur, fmtDate } from "@/lib/labels";
import { ReviewRequest } from "@/components/admin/ReviewRequest";

const STATUSES = [
  { key: "nieuw", label: "Nieuw" },
  { key: "bezocht", label: "Bezocht" },
  { key: "offerte_verzonden", label: "Offerte verzonden" },
  { key: "afgerond", label: "Afgerond" },
];

const TYPE_LABEL = { notification: "Melding", confirmation: "Bevestiging", manual: "Handmatig", campaign: "Campagne", review_request: "Reviewvraag" };

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-6 py-2.5 border-b border-cc-border last:border-0">
    <span className="font-body text-sm text-cc-sub">{label}</span>
    <span className="font-body text-sm font-medium text-cc-ink text-right">{value || "—"}</span>
  </div>
);

const Action = ({ href, icon: Icon, label, testid, onClick }) => (
  <a
    href={href}
    onClick={onClick}
    target={href && href.startsWith("http") ? "_blank" : undefined}
    rel="noopener noreferrer"
    data-testid={testid}
    className="inline-flex items-center gap-2 rounded-xl border border-cc-border bg-cc-panel2/50 px-4 py-2.5 font-body text-sm font-medium text-cc-sub hover:border-cc-accent hover:text-cc-accent transition-colors"
  >
    <Icon className="w-4 h-4" /> {label}
  </a>
);

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sendingMail, setSendingMail] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const aiSummarize = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/ai/lead-summary", { lead_id: id }, { headers: authHeaders() });
      setAiSummary(data.text || "");
    } catch { toast.error("AI-samenvatting mislukt."); }
    finally { setAiLoading(false); }
  };

  const loadEmails = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/leads/${id}/emails`, { headers: authHeaders() });
      setEmails(data);
    } catch { /* ignore */ }
  }, [id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/leads/${id}`, { headers: authHeaders() });
      setLead(data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else if (e?.response?.status === 404) toast.error("Lead niet gevonden.");
    } finally {
      setLoading(false);
    }
  }, [id, logout]);

  useEffect(() => { load(); loadEmails(); }, [load, loadEmails]);

  const setStatus = async (status) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/leads/${id}/status`, { status }, { headers: authHeaders() });
      setLead(data);
      toast.success("Status bijgewerkt.");
    } catch {
      toast.error("Bijwerken mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/admin/leads/${id}/pdf`, { headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `VHM-Renovation-aanvraag-${lead?.achternaam || "lead"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF genereren mislukt.");
    } finally {
      setPdfLoading(false);
    }
  };

  const sendMail = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Vul een onderwerp en bericht in.");
      return;
    }
    setSendingMail(true);
    try {
      await api.post(`/admin/leads/${id}/email`, { subject, body }, { headers: authHeaders() });
      toast.success("E-mail verzonden.");
      setSubject("");
      setBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versturen mislukt.");
    } finally {
      setSendingMail(false);
      loadEmails();
    }
  };

  const viewEmail = (html) => {
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); }
    else toast.error("Sta pop-ups toe om de e-mail te bekijken.");
  };

  const backAction = (
    <button onClick={() => navigate("/admin/leads")} data-testid="detail-back" className="inline-flex items-center gap-2 rounded-xl border border-cc-border px-3.5 py-2 font-body text-sm font-medium text-cc-sub hover:border-cc-accent hover:text-cc-accent transition-colors">
      <ArrowLeft className="w-4 h-4" /> Leads
    </button>
  );

  if (loading) {
    return (
      <AdminLayout title="Lead" actions={backAction}>
        <div className="flex items-center justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-cc-accent" /></div>
      </AdminLayout>
    );
  }
  if (!lead) {
    return (
      <AdminLayout title="Lead" actions={backAction}>
        <div className="flex flex-col items-center justify-center gap-4 py-24">
          <p className="font-body text-cc-faint">Lead niet gevonden.</p>
          <button onClick={() => navigate("/admin/leads")} className="text-cc-accent font-semibold">Terug naar leads</button>
        </div>
      </AdminLayout>
    );
  }

  const digits = (lead.telefoon || "").replace(/\D/g, "");
  const naam = `${lead.voornaam} ${lead.achternaam}`;
  const streetPart = [lead.straat, lead.huisnummer].filter(Boolean).join(" ");
  const cityPart = [lead.postcode, lead.gemeente].filter(Boolean).join(" ");
  const adres = [streetPart, cityPart].filter(Boolean).join(", ");
  const bd = lead.score_breakdown || {};

  return (
    <AdminLayout title={naam} subtitle={fmtDate(lead.created_at)} actions={backAction}>
      <Panel className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <ScoreBadge score={lead.score} category={lead.category} />
            <div>
              <h1 className="font-heading font-extrabold text-2xl text-cc-ink" data-testid="detail-name">{naam}</h1>
              <div className="mt-1.5 flex items-center gap-2">
                <CategoryBadge category={lead.category} testid="detail-category" />
                <span className="font-body text-sm text-cc-faint">{fmtDate(lead.created_at)}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={downloadPdf} disabled={pdfLoading} data-testid="detail-pdf" className="inline-flex items-center gap-2 rounded-xl bg-cc-accent text-white px-4 py-2.5 font-body text-sm font-semibold hover:bg-cc-accenth transition-colors disabled:opacity-60">
              {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF
            </button>
            <Action href={`tel:${lead.telefoon}`} icon={Phone} label="Bellen" testid="action-call" />
            <Action href={`mailto:${lead.email}`} icon={Mail} label="E-mail" testid="action-email" />
            {digits && <Action href={`https://wa.me/${digits}`} icon={MessageCircle} label="WhatsApp" testid="action-whatsapp" />}
            <Action href={`mailto:${lead.email}?subject=Plaatsbezoek%20VHM%20Renovation`} icon={CalendarClock} label="Plaatsbezoek" testid="action-visit" />
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-cc-border">
          <p className="font-body text-sm font-medium text-cc-sub mb-3">Status wijzigen</p>
          <div className="flex flex-wrap gap-2" data-testid="status-control">
            {STATUSES.map((s) => (
              <button
                key={s.key}
                onClick={() => setStatus(s.key)}
                disabled={saving}
                data-testid={`status-${s.key}`}
                className={`rounded-full px-4 py-2 font-body text-sm font-semibold transition-colors disabled:opacity-60 ${
                  lead.status === s.key ? "bg-cc-accent text-white" : "border border-cc-border text-cc-sub hover:border-cc-accent hover:text-cc-accent"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          {lead.status === "afgerond" && <ReviewRequest lead={lead} onSent={(at) => { setLead((l) => ({ ...l, review_requested_at: at })); loadEmails(); }} />}
        </div>
      </Panel>

      <div className="mt-5 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Panel className="p-6">
            <h2 className="font-heading font-bold text-lg text-cc-ink mb-4">Project</h2>
            <Row label="Projecttype" value={(lead.project_types || []).map(L.projectType).join(", ")} />
            <Row label="Renovatie" value={lead.renovatie_type ? L.renoType(lead.renovatie_type) : "—"} />
            <Row label="Oppervlakte" value={lead.oppervlakte ? `${lead.oppervlakte} m²` : "—"} />
            <Row label="Verdiepingen" value={lead.verdiepingen} />
            <Row label="Kamers" value={lead.kamers} />
            <Row label="Bouwjaar" value={lead.bouwjaar} />
            <Row label="Bewoond" value={lead.bewoond ? L.bewoond(lead.bewoond) : "—"} />
            <Row label="Budget" value={<span>{L.budget(lead.budget)} <span className="text-cc-faint">({eur(lead.geschatte_waarde)})</span></span>} />
            <Row label="Start" value={L.timing(lead.starttermijn)} />
            <Row label="Deadline" value={lead.heeft_deadline ? (lead.deadline || "Ja") : "Nee"} />
            <Row label="Voorkeur plaatsbezoek" value={lead.plaatsbezoek_datum ? fmtDate(lead.plaatsbezoek_datum) : "—"} />
            {(lead.works || []).length > 0 && (
              <div className="pt-4">
                <p className="font-body text-sm text-cc-sub mb-2">Werken</p>
                <div className="flex flex-wrap gap-2">
                  {lead.works.map((k) => (
                    <span key={k} className="rounded-full bg-cc-panel2 px-3 py-1 font-body text-xs text-cc-ink border border-cc-border">{L.work(k)}</span>
                  ))}
                </div>
              </div>
            )}
            {lead.beschrijving && (
              <div className="pt-4">
                <p className="font-body text-sm text-cc-sub mb-2">Omschrijving</p>
                <p className="font-body text-sm text-cc-ink bg-cc-panel2 rounded-xl p-4 leading-relaxed border border-cc-border">{lead.beschrijving}</p>
              </div>
            )}
          </Panel>

          <Panel className="p-6">
            <h2 className="font-heading font-bold text-lg text-cc-ink mb-4">Foto's & plannen</h2>
            {(lead.files || []).length === 0 ? (
              <p className="font-body text-sm text-cc-faint">Geen bestanden geüpload.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="detail-media">
                {lead.files.map((f) => {
                  const isImg = (f.content_type || "").startsWith("image/");
                  const url = fileUrl(f.storage_path);
                  return isImg ? (
                    <a key={f.id} href={url} target="_blank" rel="noopener noreferrer" className="group block rounded-xl overflow-hidden border border-cc-border aspect-square">
                      <img src={url} alt={f.original_filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </a>
                  ) : (
                    <a key={f.id} href={url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center gap-2 rounded-xl border border-cc-border aspect-square bg-cc-panel2 text-cc-sub hover:text-cc-accent hover:border-cc-accent transition-colors p-3">
                      <FileText className="w-7 h-7" />
                      <span className="text-[11px] text-center truncate w-full">{f.original_filename}</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  );
                })}
              </div>
            )}
          </Panel>

          <Panel className="p-6" data-testid="email-section">
            <h2 className="font-heading font-bold text-lg text-cc-ink mb-1">E-mail naar klant</h2>
            <p className="font-body text-sm text-cc-faint mb-4">Wordt verzonden met de professionele VHM Renovation-handtekening.</p>
            <input
              data-testid="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Onderwerp"
              className={`${inputCls} mb-3`}
            />
            <textarea
              data-testid="email-body"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={`Beste ${lead.voornaam},\n\n…`}
              className={`${inputCls} resize-none`}
            />
            <div className="mt-3 flex items-center justify-between">
              <span className="font-body text-xs text-cc-faint">Aan: {lead.email}</span>
              <button onClick={sendMail} disabled={sendingMail} data-testid="email-send" className="inline-flex items-center gap-2 rounded-xl bg-cc-accent text-white px-5 py-2.5 font-body text-sm font-semibold hover:bg-cc-accenth transition-colors disabled:opacity-60">
                {sendingMail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Versturen
              </button>
            </div>

            <div className="mt-6 pt-5 border-t border-cc-border">
              <p className="font-body text-sm font-medium text-cc-sub mb-3">Verzonden e-mails</p>
              {emails.length === 0 ? (
                <p className="font-body text-sm text-cc-faint">Nog geen e-mails.</p>
              ) : (
                <div className="space-y-2" data-testid="email-history">
                  {emails.map((m) => (
                    <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl border border-cc-border bg-cc-panel2/50 px-3 py-2.5">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="rounded-full bg-cc-panel2 px-2 py-0.5 font-body text-[10px] font-semibold uppercase text-cc-faint border border-cc-border">{TYPE_LABEL[m.type] || m.type}</span>
                          <span className={`font-body text-[11px] font-semibold ${m.status === "verzonden" ? "text-cc-pos" : "text-cc-hot"}`}>{m.status}</span>
                        </div>
                        <p className="font-body text-sm text-cc-ink truncate mt-1">{m.subject}</p>
                        <p className="font-body text-xs text-cc-faint">{fmtDate(m.created_at)}</p>
                      </div>
                      <button onClick={() => viewEmail(m.html)} data-testid={`email-view-${m.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-cc-border px-3 py-2 font-body text-xs font-medium text-cc-sub hover:border-cc-accent hover:text-cc-accent transition-colors shrink-0">
                        <Eye className="w-3.5 h-3.5" /> Bekijk
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Panel>
        </div>

        <div className="space-y-5">
          <Panel className="p-6">
            <h2 className="font-heading font-bold text-lg text-cc-ink mb-4">Contact</h2>
            <Row label="Naam" value={naam} />
            <Row label="Telefoon" value={lead.telefoon} />
            <Row label="E-mail" value={lead.email} />
            <Row label="Bedrijf" value={lead.bedrijfsnaam} />
            <Row label="BTW" value={lead.btw} />
            <div className="pt-3 flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-cc-accent" />
              <span className="font-body text-sm text-cc-ink">{adres || "—"}</span>
            </div>
            {lead.opmerkingen && (
              <p className="mt-3 font-body text-sm text-cc-ink bg-cc-panel2 rounded-xl p-3 border border-cc-border">{lead.opmerkingen}</p>
            )}
          </Panel>

          <Panel className="p-6">
            <h2 className="font-heading font-bold text-lg text-cc-ink mb-4">Leadscore</h2>
            <div className="text-center mb-4">
              <p className="font-heading font-extrabold text-4xl text-cc-ink">{lead.score}<span className="text-cc-faint text-xl">/100</span></p>
              <div className="mt-1 flex justify-center"><CategoryBadge category={lead.category} /></div>
            </div>
            <Row label="Budget" value={`${bd.budget ?? "—"} / 30`} />
            <Row label="Projecttype" value={`${bd.project ?? "—"} / 25`} />
            <Row label="Locatie" value={`${bd.location ?? "—"} / 20`} />
            <Row label="Timing" value={`${bd.timing ?? "—"} / 15`} />
            <Row label="Compleetheid" value={`${bd.completeness ?? "—"} / 10`} />
            <button onClick={aiSummarize} disabled={aiLoading} data-testid="ai-summary-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-cc-border bg-cc-panel2 px-4 py-2.5 font-body text-sm font-semibold text-cc-ink hover:border-cc-accent transition-colors disabled:opacity-60">
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-cc-accent" />} AI-samenvatting
            </button>
            {aiSummary && <p data-testid="ai-summary-text" className="mt-3 font-body text-sm text-cc-ink bg-cc-panel2 rounded-xl p-3 border border-cc-border whitespace-pre-line">{aiSummary}</p>}
          </Panel>
        </div>
      </div>
    </AdminLayout>
  );
}
